import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Users, TrendingUp, Award, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dealer = () => {
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    business_type: '',
    experience: '',
    message: '',
    website: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/dealer-enquiry`, formData);
      toast.success('Enquiry Submitted!', {
        description: response.data.message
      });
      setFormData({
        name: '',
        business_name: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        business_type: '',
        experience: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting dealer enquiry:', error);
      toast.error('Failed to submit enquiry', {
        description: 'Please try again or contact us directly.'
      });
    }
  };

  const benefits = [
    { icon: Award, title: 'Premium Products', description: 'Industry-leading 6H hardness acrylic boards and innovative door solutions' },
    { icon: TrendingUp, title: 'High Margins', description: 'Attractive profit margins and competitive dealer pricing' },
    { icon: Users, title: 'Marketing Support', description: 'Sales materials, training, and promotional support for your business' },
    { icon: MapPin, title: 'Territory Rights', description: 'Exclusive dealership opportunities in select regions' }
  ];

  const whoShouldApply = [
    { title: 'Interior Material Dealers', description: 'Existing dealers in laminates, plywoods, or interior materials' },
    { title: 'Hardware & Building Material Distributors', description: 'Distributors with established networks in construction materials' },
    { title: 'Furniture & Kitchen Manufacturers', description: 'Modular kitchen and furniture manufacturers looking for premium materials' },
    { title: 'New Entrepreneurs', description: 'Business-minded individuals ready to enter the interior materials industry' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Partnership Opportunity</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Become a CriLam Dealer / Distributor
            </h1>
            <p className="text-xl text-white/70 max-w-2xl">
              Partner with India's leading manufacturer of premium acrylic laminated boards and interior doors
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Why Join Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] font-['Outfit']">Why Partner with CriLam?</h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-8 bg-[#F7F8F5] border border-black/5 text-center hover:border-[#D4AF37]/30 transition-colors group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#004534] mx-auto mb-6 group-hover:bg-[#D4AF37] transition-colors">
                  <benefit.icon className="h-8 w-8 text-[#D4AF37] group-hover:text-[#004534] transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-[#004534] mb-3">{benefit.title}</h3>
                <p className="text-[#525956] text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Who Should Apply */}
      <section className="py-20 lg:py-28 bg-[#0A0D0C] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Ideal Partners</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit']">Who Should Apply?</h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {whoShouldApply.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="flex items-start gap-4 p-6 bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06] transition-colors"
              >
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#D4AF37]">
                  <CheckCircle className="h-5 w-5 text-[#004534]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#F7F8F5] border border-black/5 p-8 lg:p-12"
            >
              <div className="text-center mb-10">
                <p className="label-mono text-[#D4AF37] mb-2">Apply Now</p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#004534] font-['Outfit']">Dealer Enquiry Form</h2>
                <p className="text-[#525956] mt-2">Fill out the form below and our team will get in touch with you</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="dealer-form">
                <input type="text" name="website" value={formData.website} onChange={handleChange} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#004534] font-medium">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_name" className="text-[#004534] font-medium">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      type="text"
                      value={formData.business_name}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-business-name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-[#004534] font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#004534] font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-phone"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="city" className="text-[#004534] font-medium">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-[#004534] font-medium">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      type="text"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-state"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="business_type" className="text-[#004534] font-medium">Business Type *</Label>
                    <Input
                      id="business_type"
                      name="business_type"
                      type="text"
                      placeholder="e.g., Dealer, Distributor, Manufacturer"
                      value={formData.business_type}
                      onChange={handleChange}
                      required
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-business-type"
                    />
                  </div>
                  <div>
                    <Label htmlFor="experience" className="text-[#004534] font-medium">Years of Experience</Label>
                    <Input
                      id="experience"
                      name="experience"
                      type="text"
                      placeholder="e.g., 5 years"
                      value={formData.experience}
                      onChange={handleChange}
                      className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="dealer-experience"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#004534] font-medium">Additional Information</Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={4}
                    placeholder="Tell us about your business and why you'd like to partner with CriLam..."
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="dealer-message"
                  />
                </div>

                <motion.button
                  type="submit"
                  className="w-full py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  data-testid="dealer-submit"
                >
                  Submit Enquiry
                  <ArrowRight className="h-4 w-4" />
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dealer;
